import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, User, Phone, Calendar, MapPin, ArrowRight, X } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "tnt_intro_lead_submitted";
const SESSION_KEY = "tnt_intro_dismissed";
const SKIP_PREFIXES = ["/admin", "/login", "/signup"];

const DEFAULTS = {
  enabled: true,
  title: "Before you step in, tell us about your celebration.",
  subtitle: "A curator will personally reach out with décor ideas tuned to your date and venue.",
  eyebrow: "Tent N Tap · Curated",
  name_label: "Full name",
  mobile_label: "Mobile number",
  date_label: "Event date",
  venue_label: "Event venue / city",
  submit_label: "Enter the marketplace",
  footer_note: "Your details stay private · used only for curator follow-up",
};

export default function IntroGate() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULTS);
  const [form, setForm] = useState({ name: "", mobile: "", event_date: "", event_venue: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) { setOpen(false); return; }
    // Permanent skip ONLY when they've actually submitted the form
    const submitted = localStorage.getItem(STORAGE_KEY);
    if (submitted) return;
    // Session-only skip when they've dismissed via ✕ this browser session
    const dismissedThisSession = sessionStorage.getItem(SESSION_KEY);
    if (dismissedThisSession) return;
    api.get("/settings/intro").then((r) => {
      setSettings({ ...DEFAULTS, ...r.data });
      if (r.data?.enabled !== false) setOpen(true);
    }).catch(() => { setOpen(true); });
  }, [pathname]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const mobile = form.mobile.replace(/\s+/g, "");
    if (!/^\+?\d{6,15}$/.test(mobile)) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/intro-leads", { ...form, mobile });
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), name: form.name }));
      toast.success(`Welcome ${form.name.split(" ")[0]}! Explore our curated worlds.`);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  const dismiss = () => {
    // Dismissal is session-only — a new visitor / new browser session sees the popup again.
    // Only submitting the form permanently marks the visitor as leaded.
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div data-testid="intro-gate" className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#292724]/70 backdrop-blur-md animate-in fade-in duration-500">
      <div className="relative w-full max-w-xl overflow-hidden rounded-3xl bg-[#fcfaf8] shadow-[0_40px_120px_rgba(0,0,0,0.35)] border border-[#d4af37]/30">
        <button
          data-testid="intro-close"
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-lg flex items-center justify-center text-[#292724] hover:text-[#d4af37] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Gold blob accents */}
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#d4af37]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-[#f4e5b7]/40 blur-3xl pointer-events-none" />

        {/* Header band */}
        <div className="relative bg-[#292724] text-white px-8 pt-8 pb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-px bg-[#d4af37]" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]">{settings.eyebrow}</span>
          </div>
          <h2 data-testid="intro-title" className="font-serif text-3xl lg:text-4xl font-light leading-tight">
            {settings.title}
          </h2>
          <p data-testid="intro-subtitle" className="mt-3 text-sm text-white/70 max-w-md">
            {settings.subtitle}
          </p>
        </div>

        {/* Form body */}
        <form onSubmit={submit} className="relative px-8 py-7 space-y-4" data-testid="intro-gate-form">
          <div className="relative">
            <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a837a]" />
            <input
              data-testid="intro-name"
              required
              placeholder={settings.name_label}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-[#ebe6e0] focus:border-[#d4af37] outline-none rounded-full pl-10 pr-4 py-3 text-sm bg-white transition-colors"
            />
          </div>

          <div className="relative">
            <Phone className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a837a]" />
            <input
              data-testid="intro-mobile"
              required
              type="tel"
              inputMode="tel"
              placeholder={settings.mobile_label}
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              className="w-full border border-[#ebe6e0] focus:border-[#d4af37] outline-none rounded-full pl-10 pr-4 py-3 text-sm bg-white transition-colors"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <Calendar className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a837a] pointer-events-none" />
              <input
                data-testid="intro-event-date"
                required
                type="date"
                title={settings.date_label}
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="w-full border border-[#ebe6e0] focus:border-[#d4af37] outline-none rounded-full pl-10 pr-4 py-3 text-sm bg-white transition-colors"
              />
            </div>
            <div className="relative">
              <MapPin className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a837a]" />
              <input
                data-testid="intro-event-venue"
                required
                placeholder={settings.venue_label}
                value={form.event_venue}
                onChange={(e) => setForm({ ...form, event_venue: e.target.value })}
                className="w-full border border-[#ebe6e0] focus:border-[#d4af37] outline-none rounded-full pl-10 pr-4 py-3 text-sm bg-white transition-colors"
              />
            </div>
          </div>

          <button
            data-testid="intro-submit"
            type="submit"
            disabled={submitting}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#d4af37] hover:bg-[#c9a233] text-[#292724] font-medium py-3.5 text-sm transition-all shadow-lg hover:shadow-xl disabled:opacity-60"
          >
            {submitting ? "One moment…" : (<>{settings.submit_label} <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <div className="flex items-center justify-center gap-2 pt-1 text-[11px] text-[#8a837a]">
            <Sparkles className="w-3 h-3 text-[#d4af37]" />
            {settings.footer_note}
          </div>
        </form>
      </div>
    </div>
  );
}
